import { accountTypeSchema, addressSchema, associationTypeSchema, financialReportingFrequencySchema, mfaRequirementSchema, ownershipAllocationDeltaSchema, ownershipProfileTypeSchema, ownershipRoleSchema, ownershipSharesSchema, portfolioTypeSchema, } from '@teo/types';
import { z } from 'zod';
import { displayId, documentCategorizationSchema } from './common.js';
import { unitAdditionalDataReadSchema } from './properties.js';
const individualProfileSchema = z.object({
    ownershipProfileType: z.literal('individual'),
    firstName: z.string().min(1),
    middleInitial: z.string().max(1).nullable(),
    lastName: z.string().min(1),
    ssn: z.string().regex(/^\d{3}-\d{2}-\d{4}$/, 'SSN must be in format 000-00-0000'),
});
const businessProfileSchema = z.object({
    ownershipProfileType: z.enum(['llc', 'trust']),
    businessName: z.string().min(1),
    legalEntityName: z.string().nullable(),
    dba: z.string().nullable(),
    ein: z.string().regex(/^\d{2}-\d{7}$/, 'EIN must be in format 00-0000000'),
});
const associationProfileSchema = z.object({
    ownershipProfileType: z.literal('association'),
    businessName: z.string().min(1),
    legalEntityName: z.string().nullable(),
    dba: z.string().nullable(),
    ein: z.string().regex(/^\d{2}-\d{7}$/, 'EIN must be in format 00-0000000'),
    associationType: associationTypeSchema,
});
export const ownershipProfileSchema = z.discriminatedUnion('ownershipProfileType', [
    individualProfileSchema,
    businessProfileSchema,
    associationProfileSchema,
]);
export const ownershipPreferencesSchema = z.object({
    propertyAlerts: z.boolean(),
    billingUpdates: z.boolean(),
    complianceReminders: z.boolean(),
    financialReporting: financialReportingFrequencySchema,
    violations: z.boolean(),
    pmApprovalLimit: z.number().nonnegative().nullable(),
});
export const ownershipBillingSchema = z.object({
    planName: z.string().nullable(),
    renewalDate: z.date().nullable(),
    paymentMethod: z.enum(['Credit Card', 'Bank Account']).nullable(),
});
export const createOwnershipSchema = z.object({
    email: z.string().email(),
    phoneNumber: z.string().min(1),
    faxNumber: z.string().optional(),
    mailingAddress: addressSchema,
    preferences: ownershipPreferencesSchema.partial().optional(),
    billing: ownershipBillingSchema.optional(),
    profile: ownershipProfileSchema,
});
export const updateOwnershipSchema = z.object({
    ownershipId: displayId,
    email: z.string().email().optional(),
    phoneNumber: z.string().min(1).optional(),
    faxNumber: z.string().optional(),
    mailingAddress: addressSchema.optional(),
    profile: ownershipProfileSchema.optional(),
    preferences: ownershipPreferencesSchema.partial().optional(),
    billing: ownershipBillingSchema.partial().optional(),
});
export const updateOwnershipContactSchema = z.object({
    ownershipId: displayId,
    email: z.string().email().optional().describe('The new email of the ownership. Omit to keep it.'),
    phoneNumber: z
        .string()
        .min(1)
        .optional()
        .describe('The new phone number of the ownership. Omit to keep it.'),
    faxNumber: z.string().optional().describe('The new fax number. Omit to keep it.'),
    mailingAddress: addressSchema
        .optional()
        .describe('The new mailing address, with every line. Omit to keep it.'),
});
export const updateOwnershipPreferencesSchema = z.object({
    ownershipId: displayId,
    preferences: ownershipPreferencesSchema.partial(),
});
export const updatePaymentMethodSchema = z.object({
    ownershipId: displayId,
    paymentMethod: z.enum(['Credit Card', 'Bank Account']),
});
export const deleteOwnershipSchema = z.object({
    ownershipId: displayId,
});
export const hasOwnershipIdSchema = z.object({
    ownershipId: displayId,
});
export const getBankAccountsSchema = hasOwnershipIdSchema;
export const bankAccountFieldsSchema = z.object({
    accountType: accountTypeSchema,
    bankName: z.string().min(1, 'Bank name is required').max(255),
    routingNumber: z
        .string()
        .length(9, 'Routing number must be 9 digits')
        .regex(/^\d+$/, 'Routing number must contain only digits'),
    accountNumber: z.string().min(1, 'Account number is required').max(255),
});
export const createBankAccountSchema = bankAccountFieldsSchema.extend({
    ownershipId: displayId,
});
export const updateBankAccountSchema = bankAccountFieldsSchema.extend({
    id: displayId,
    ownershipId: displayId,
});
export const deleteBankAccountSchema = z.object({
    id: displayId,
    ownershipId: displayId,
});
export const getOwnershipPermissionsSchema = hasOwnershipIdSchema;
export const removeOwnershipPermissionSchema = z.object({
    userDisplayId: displayId,
    ownershipId: displayId,
});
export const addOwnershipPermissionSchema = z.object({
    ownershipId: displayId,
    userId: displayId,
    profileType: ownershipRoleSchema,
});
export const getOwnershipPortfoliosSchema = hasOwnershipIdSchema;
export const associatePortfolioSchema = z.object({
    portfolioId: displayId,
    ownershipId: displayId,
});
export const dissociatePortfolioSchema = z.object({
    ownershipId: displayId,
    portfolioId: displayId,
});
export const movePortfolioSchema = z.object({
    portfolioId: displayId,
    fromOwnershipId: displayId,
    toOwnershipId: displayId.optional(),
});
export const getOwnershipDocumentsSchema = hasOwnershipIdSchema.extend({
    minutesAgo: z
        .number()
        .optional()
        .describe('Only documents uploaded within this many minutes. Omit for all of them.'),
});
export const updateOwnershipPercentagesSchema = z
    .object({
    ownershipId: displayId,
    ownershipDelta: ownershipAllocationDeltaSchema,
})
    .refine((data) => {
    const sum = Object.values(data.ownershipDelta).reduce((acc, cur) => acc + cur, 0);
    return sum === 0;
}, {
    message: 'Ownership percentage changes must sum to zero',
    path: ['ownershipDelta'],
});
export const getOwnershipInvitesSchema = hasOwnershipIdSchema;
export const bankAccountOutputSchema = z.object({
    id: displayId,
    ownershipId: z.number(),
    accountType: accountTypeSchema,
    bankName: z.string(),
    routingNumber: z.string(),
    accountNumber: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const ownershipProfileSummarySchema = z.discriminatedUnion('ownershipProfileType', [
    individualProfileSchema.omit({ ssn: true }),
    businessProfileSchema.omit({ ein: true }),
    associationProfileSchema.omit({ ein: true }),
]);
export const bankAccountSummarySchema = bankAccountOutputSchema
    .pick({ id: true, accountType: true, bankName: true })
    .extend({ maskedAccountNumber: z.string() });
export const setBoardMfaRequirementSchema = z.object({
    ownershipId: displayId,
    requirement: mfaRequirementSchema,
});
export const setBoardMfaRequirementOutputSchema = z.object({
    requirement: mfaRequirementSchema,
    resynced: z.number().int().nonnegative(),
});
export const ownershipOutputSchema = z.object({
    id: displayId,
    createdByUserId: z.number().nullable(),
    email: z.string(),
    phoneNumber: z.string(),
    faxNumber: z.string().optional(),
    mailingAddress: addressSchema,
    businessName: z.string().optional(),
    profile: ownershipProfileSchema,
    preferences: ownershipPreferencesSchema,
    billing: ownershipBillingSchema,
    boardMfaRequirement: mfaRequirementSchema,
    ownershipShares: ownershipSharesSchema,
    bankAccounts: z.array(bankAccountOutputSchema).optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const ownershipSummaryOutputSchema = ownershipOutputSchema
    .pick({
    id: true,
    email: true,
    phoneNumber: true,
    faxNumber: true,
    mailingAddress: true,
    businessName: true,
    preferences: true,
    billing: true,
    boardMfaRequirement: true,
})
    .extend({
    profile: ownershipProfileSummarySchema,
    bankAccounts: z.array(bankAccountSummarySchema),
});
export const ownershipContactOutputSchema = ownershipSummaryOutputSchema.pick({
    id: true,
    email: true,
    phoneNumber: true,
    faxNumber: true,
    mailingAddress: true,
});
export const userOwnershipOutputSchema = ownershipSummaryOutputSchema
    .extend(ownershipOutputSchema.pick({
    createdByUserId: true,
    ownershipShares: true,
    createdAt: true,
    updatedAt: true,
}).shape)
    .extend({ maskedTaxId: z.string().optional() });
export const getOwnershipProfileSchema = z.object({ ownershipId: displayId });
const baseUserSchema = z.object({
    userDisplayId: displayId,
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
});
export const ownershipPermissionOutputSchema = z.object({
    ownershipId: displayId,
    userId: displayId,
    role: ownershipRoleSchema,
    canManage: z.boolean().optional(),
    user: baseUserSchema,
});
export const ownershipPortfolioOutputSchema = z.object({
    ownershipId: displayId,
    portfolioId: displayId,
    name: z.string(),
    description: z.string().nullable(),
    portfolioType: portfolioTypeSchema,
    displayOrder: z.number(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const ownershipDocumentOutputSchema = z.object({
    id: displayId,
    ownershipId: z.number(),
    url: z.string(),
    filename: z.string(),
    type: z.string(),
    uploadedByUserId: z.number().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const ownershipDocumentWithCategorizationOutputSchema = ownershipDocumentOutputSchema.extend({
    categorization: documentCategorizationSchema,
});
export const getOwnershipDocumentsOutputSchema = z.array(ownershipDocumentWithCategorizationOutputSchema);
export const unitStructureSchema = z.object({
    id: z.number(),
    displayId: displayId,
    unitIdentifier: z.string().nullable(),
    additionalData: unitAdditionalDataReadSchema.nullable(),
});
export const propertyStructureSchema = z.object({
    id: z.number(),
    displayId: displayId,
    address: addressSchema,
    units: z.array(unitStructureSchema),
});
export const portfolioStructureSchema = z.object({
    id: z.number(),
    displayId: displayId,
    name: z.string(),
    portfolioType: portfolioTypeSchema,
    properties: z.array(propertyStructureSchema),
});
export const ownershipStructureSchema = z.object({
    id: z.number(),
    displayId: displayId,
    businessName: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.string(),
    ownershipProfileType: ownershipProfileTypeSchema,
    portfolios: z.array(portfolioStructureSchema),
});
export const userOwnershipStructureSchema = z.object({
    permissionId: z.number(),
    role: ownershipRoleSchema,
    ownership: ownershipStructureSchema,
});
//# sourceMappingURL=ownership.js.map